/* eslint-disable react/no-unknown-property -- Firebolt consumes the custom css prop at build time. */
import { css } from '@firebolt-dev/css'
import { useEffect, useMemo, useRef, useState } from 'react'
import { CoinsIcon, HistoryIcon, MapIcon, MapPinIcon, RefreshCwIcon, ShieldCheckIcon, ShieldIcon, UserPlusIcon, WalletCardsIcon } from 'lucide-react'

import { COLORS, ICON_SIZES, RADII, TYPE, space } from '../theme'
import { WALLET_PLOTS, refreshScreens, screenData, subscribeScreenData, walletPlots } from '../screen-data'
import { Btn, MetaStat, Panel, ScreenShell } from './VoiceKit'

const KIND_LABELS = {
  'legacy-migration': 'Начальный баланс',
  'plot-purchase': 'Покупка участка',
  'grid-buy': 'Покупка предмета',
  'grid-pay': 'Перевод игроку',
  'grid-stipend': 'Суточная выплата',
  'grid-quest': 'Награда задания',
  'grid-prod': 'Хозяйство',
  'economy-tax': 'Доход хозяйства',
}

export function formatMK(minor, scale = 100) {
  const value = Number(minor) / (Number(scale) || 100)
  return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(Number.isFinite(value) ? value : 0)} МК`
}

export function WalletPlotsScreen({ world, initialTab = 'wallet', onClose }) {
  const [state, setState] = useState(() => walletPlots(world))
  const [loaded, setLoaded] = useState(() => screenData(world, 'wallet') !== null)
  const [tab, setTab] = useState(initialTab)
  const [pending, setPending] = useState(null)
  const lastLandResult = useRef(null)
  const owned = useMemo(() => state.plots.filter(plot => plot.mine), [state.plots])
  const available = useMemo(() => state.plots.filter(plot => plot.claimable || plot.purchasable), [state.plots])

  useEffect(() => {
    const off = subscribeScreenData(world, 'wallet', () => {
      const next = walletPlots(world)
      setState(next)
      setLoaded(true)
      setPending(null)
      if (next.landResult?.requestId && next.landResult.requestId !== lastLandResult.current) {
        lastLandResult.current = next.landResult.requestId
        world.ui.notify({
          kind: next.landResult.ok ? 'success' : 'error',
          title: next.landResult.ok ? 'ПРАВА УЧАСТКА ОБНОВЛЕНЫ' : 'ПРАВА НЕ ИЗМЕНЕНЫ',
          message: next.landResult.ok ? 'Сервер подтвердил новое состояние доступа.' : `Сервер отклонил изменение: ${next.landResult.code || 'LAND_ACTION_FAILED'}.`,
          dedupeKey: `land-result:${next.landResult.requestId}`,
        })
      }
    })
    refreshScreens(world)
    return off
  }, [world])

  useEffect(() => {
    const onKey = event => { if (event.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const acquire = plot => {
    if (!WALLET_PLOTS.acquire(world, plot.id)) {
      world.ui.notify({ kind: 'error', title: 'ОПЕРАЦИЯ НЕ ОТПРАВЛЕНА', message: 'Нет соединения с сервером мира.', dedupeKey: 'wallet:offline' })
      return
    }
    setPending(plot.id)
  }

  return <ScreenShell
    label='WORLD'
    title={tab === 'wallet' ? 'КОШЕЛЁК МК' : 'УЧАСТКИ МИРА'}
    subtitle='Только внутриигровая валюта. Баланс, история и владение подтверждаются сервером.'
    ariaLabel='Кошелёк и участки TESTCS World'
    onClose={onClose}
    meta={<>
      <MetaStat icon={<CoinsIcon size={ICON_SIZES.sm} color={COLORS.warning} />} label='БАЛАНС' value={formatMK(state.balanceMinor, state.scale)} />
      <MetaStat icon={<MapPinIcon size={ICON_SIZES.sm} color={COLORS.cyan} />} label='МОИ УЧАСТКИ' value={String(owned.length)} />
    </>}
  >
    <div css={css`display:flex;gap:${space(1)};padding:${space(2)} ${space(2)} 0;`}>
      <Btn variant={tab === 'wallet' ? 'primary' : 'secondary'} onClick={() => setTab('wallet')}><WalletCardsIcon size={ICON_SIZES.xs} /> КОШЕЛЁК</Btn>
      <Btn variant={tab === 'plots' ? 'primary' : 'secondary'} onClick={() => setTab('plots')}><MapIcon size={ICON_SIZES.xs} /> УЧАСТКИ</Btn>
      <Btn variant='ghost' onClick={() => WALLET_PLOTS.refresh(world)}><RefreshCwIcon size={ICON_SIZES.xs} /> ОБНОВИТЬ</Btn>
    </div>

    <div css={css`flex:1;min-height:0;padding:${space(2)};overflow-y:auto;`}>
      {!loaded ? <StateCard title='ЗАГРУЖАЕМ ДАННЫЕ' text='Запрашиваем баланс и участки у сервера мира.' />
        : !state.available ? <StateCard title='КОШЕЛЁК НЕДОСТУПЕН' text='Сервер не подтвердил состояние. Локальные демонстрационные суммы не показываются.' action={() => refreshScreens(world)} />
          : tab === 'wallet' ? <WalletView state={state} /> : <PlotsView world={world} state={state} available={available} pending={pending} acquire={acquire} />}
    </div>
  </ScreenShell>
}

function WalletView({ state }) {
  return <div css={twoColumnCss}>
    <Panel title='ТЕКУЩИЙ БАЛАНС' info='МК — игровые единицы TESTCS World. Вывод, криптовалюта и внешние платежи не поддерживаются.'>
      <div css={css`min-height:180px;display:grid;place-items:center;align-content:center;gap:${space(1)};text-align:center;`}>
        <CoinsIcon size={ICON_SIZES.hero} color={COLORS.warning} />
        <strong css={css`font-size:${TYPE.hero.size};font-variant-numeric:tabular-nums;`}>{formatMK(state.balanceMinor, state.scale)}</strong>
        <span css={mutedCss}>SERVER-AUTHORITATIVE · INTEGER MINOR UNITS</span>
        <div css={safeCss}><ShieldCheckIcon size={ICON_SIZES.xs} /> Нет cash-out, реальных денег и криптофункций</div>
      </div>
    </Panel>
    <Panel title={`ИСТОРИЯ · ${state.history.length}`} info='Каждая запись имеет уникальный transaction ID; повторы с тем же ключом не списываются повторно.'>
      {state.history.length ? <div css={listCss}>{state.history.map(item => <div key={item.id} css={rowCss}>
        <HistoryIcon size={ICON_SIZES.xs} color={item.deltaMinor >= 0 ? COLORS.success : COLORS.warning} />
        <div css={css`min-width:0;flex:1;`}><strong>{KIND_LABELS[item.kind] || item.kind}</strong><div css={mutedCss}>{new Date(item.createdAt).toLocaleString('ru-RU')} · {String(item.id).slice(0, 8)}</div></div>
        <strong css={css`color:${item.deltaMinor >= 0 ? COLORS.success : COLORS.warning};font-variant-numeric:tabular-nums;`}>{item.deltaMinor >= 0 ? '+' : '−'}{formatMK(Math.abs(item.deltaMinor), state.scale)}</strong>
      </div>)}</div> : <StateCard compact title='ИСТОРИЯ ПОКА ПУСТА' text='Здесь появятся только подтверждённые сервером операции.' />}
    </Panel>
  </div>
}

function PlotsView({ world, state, available, pending, acquire }) {
  if (!state.plots.length) return <StateCard title='НЕТ AUTHORITATIVE УЧАСТКОВ' text='Build-authority не опубликовал parcel records. Экран не создаёт фиктивную карту.' />
  return <div css={css`display:grid;gap:${space(2)};`}>
    <Panel title={`МОИ УЧАСТКИ · ${state.plots.filter(plot => plot.mine).length}`}>
      <PlotGrid plots={state.plots.filter(plot => plot.mine)} state={state} empty='У тебя пока нет участка.' />
    </Panel>
    {state.plots.filter(plot => plot.mine).map(plot => <PermissionPanel key={`permissions:${plot.id}`} world={world} state={state} plot={plot} />)}
    {state.landPermissions.access.length > 0 && <Panel title='ПРАВА НА ЧУЖИХ УЧАСТКАХ' info='Это только явно выданные владельцами права Build Authority.'><div css={listCss}>{state.landPermissions.access.map(item => <div key={item.plotId} css={rowCss}><ShieldIcon size={ICON_SIZES.xs} color={COLORS.cyan} /><strong>Участок #{item.plotId}</strong><span css={mutedCss}>{item.capabilities.join(' · ')}</span></div>)}</div></Panel>}
    <Panel title={`ДОСТУПНЫЕ · ${available.length}`} info='Цена берётся только из серверной записи. Свободный участок закрепляется бесплатно; занятый можно купить лишь при активной продаже.'>
      <PlotGrid world={world} plots={available} state={state} pending={pending} acquire={acquire} empty='Свободных или выставленных на продажу участков сейчас нет.' />
    </Panel>
    <Panel title='ВСЕ PARCEL RECORDS'><PlotGrid plots={state.plots} state={state} compact /></Panel>
  </div>
}

function PermissionPanel({ world, state, plot }) {
  const players = state.landPermissions.availablePlayers
  const [subjectUserId, setSubjectUserId] = useState(() => players[0]?.userId || '')
  useEffect(() => {
    if (players.some(player => player.userId === subjectUserId)) return
    setSubjectUserId(players[0]?.userId || '')
  }, [players, subjectUserId])
  const grants = state.landPermissions.grants.filter(item => item.plotId === plot.id)
  const selected = players.find(player => player.userId === subjectUserId)
  const mutate = async (capability, active) => {
    if (!selected) return
    const confirmed = await world.ui.confirm({
      kind: 'security', tone: active ? 'warning' : 'info', safeEnter: false,
      title: active ? 'ОТОЗВАТЬ ПРАВО?' : 'ВЫДАТЬ ПРАВО НА УЧАСТОК?',
      message: `${selected.name}: ${capability.label}. Изменение проверит владелец и версия участка #${plot.index}.`,
      dedupeKey: `land-permission:${plot.id}:${selected.userId}:${capability.id}:${active ? 'revoke' : 'grant'}`,
    })
    if (!confirmed) return
    const sent = active
      ? WALLET_PLOTS.revoke(world, plot, selected.userId, capability.id)
      : WALLET_PLOTS.grant(world, plot, selected.userId, capability.id)
    if (!sent) world.ui.notify({ kind: 'error', title: 'НЕТ СОЕДИНЕНИЯ', message: 'Изменение прав не отправлено.', dedupeKey: 'land-permission:offline' })
  }
  return <Panel title={`ПРАВА УЧАСТКА #${plot.index}`} info='Владелец может выдать только действия, которые реально проверяет Build Authority. Право собственности не передаётся.'>
    {!state.landPermissions.available ? <StateCard compact title='ПРАВА НЕДОСТУПНЫ' text='Сервер не прислал authoritative permission state.' />
      : !players.length ? <StateCard compact title='НЕКОГО ДОБАВИТЬ' text='Выдать право можно только существующему игроку, который сейчас доступен серверу мира.' />
        : <div css={css`display:grid;gap:${space(2)};`}>
          <label css={css`display:grid;gap:${space(1)};color:${COLORS.textSecondary};font-size:${TYPE.caption.size};`}><span><UserPlusIcon size={ICON_SIZES.xs} /> ИГРОК</span><select aria-label='Игрок для прав участка' value={subjectUserId} onChange={event => setSubjectUserId(event.target.value)} css={selectCss}>{players.map(player => <option key={player.userId} value={player.userId}>{player.name}</option>)}</select></label>
          <div role='list' aria-label='Реальные права строительства' css={css`display:grid;gap:${space(1)};`}>
            {state.landPermissions.capabilities.map(capability => {
              const active = grants.some(item => item.subjectUserId === subjectUserId && item.capabilities.includes(capability.id))
              return <div role='listitem' key={capability.id} css={rowCss}><ShieldIcon size={ICON_SIZES.xs} color={active ? COLORS.success : COLORS.textSecondary} /><div css={css`flex:1;display:grid;gap:3px;`}><strong>{capability.label}</strong><span css={mutedCss}>{capability.source}</span></div><Btn variant={active ? 'destructive' : 'secondary'} onClick={() => mutate(capability, active)}>{active ? 'ОТОЗВАТЬ' : 'ВЫДАТЬ'}</Btn></div>
            })}
          </div>
        </div>}
  </Panel>
}

function PlotGrid({ world, plots, state, pending, acquire, empty, compact = false }) {
  if (!plots.length) return <StateCard compact title='ПУСТО' text={empty} />
  return <div css={css`display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:${space(1)};`}>
    {plots.map(plot => <article key={plot.id} css={css`${plotCss};border-color:${plot.mine ? COLORS.cyan : plot.claimable ? COLORS.success : COLORS.border};`}>
      <div css={css`display:flex;align-items:center;gap:${space(1)};`}><MapPinIcon size={ICON_SIZES.sm} color={plot.mine ? COLORS.cyan : COLORS.textSecondary} /><strong>УЧАСТОК #{plot.index}</strong></div>
      <span css={mutedCss}>Центр: {plot.origin.map(value => Number(value).toFixed(1)).join(' · ')}</span>
      <span css={statusCss(plot)}>{plot.mine ? 'ВАШ' : plot.claimable ? 'СВОБОДЕН' : plot.purchasable ? 'ПРОДАЁТСЯ' : 'ЗАНЯТ'}</span>
      {plot.ownerName && <span css={mutedCss}>Владелец: {plot.ownerName}</span>}
      {plot.priceMinor !== null && !plot.mine && <strong>{formatMK(plot.priceMinor, state.scale)}</strong>}
      {!compact && acquire && (plot.claimable || plot.purchasable) && <Btn full disabled={Boolean(pending)} onClick={async () => {
        const confirmed = await world.ui.confirm({ kind: 'purchase', tone: 'warning', title: plot.claimable ? 'ЗАКРЕПИТЬ УЧАСТОК?' : 'КУПИТЬ УЧАСТОК?', message: plot.claimable ? `Сервер проверит право на участок #${plot.index}.` : `Сервер спишет только установленную им цену ${formatMK(plot.priceMinor, state.scale)} за участок #${plot.index}.`, safeEnter: false, dedupeKey: `plot:${plot.id}:confirm` })
        if (confirmed) acquire(plot)
      }}>{pending === plot.id ? 'ПОДТВЕРЖДАЕМ…' : plot.claimable ? 'ЗАКРЕПИТЬ' : 'КУПИТЬ'}</Btn>}
    </article>)}
  </div>
}

function StateCard({ title, text, action, compact = false }) {
  return <div role='status' css={css`min-height:${compact ? '90px' : '180px'};display:grid;place-items:center;align-content:center;gap:${space(1)};text-align:center;color:${COLORS.textSecondary};`}>
    <strong css={css`color:${COLORS.text};letter-spacing:.1em;`}>{title}</strong><span css={css`max-width:420px;font-size:${TYPE.caption.size};`}>{text}</span>{action && <Btn onClick={action}>ПОВТОРИТЬ</Btn>}
  </div>
}

const twoColumnCss = css`display:grid;grid-template-columns:minmax(280px,.75fr) minmax(360px,1.25fr);gap:${space(2)};@media(max-width:800px){grid-template-columns:1fr;}`
const listCss = css`display:flex;flex-direction:column;gap:${space(1)};max-height:430px;overflow-y:auto;`
const rowCss = css`display:flex;align-items:center;gap:${space(1)};padding:${space(1)};border:1px solid ${COLORS.border};border-radius:${RADII.lg};background:${COLORS.bgPanelLight};`
const plotCss = css`display:flex;flex-direction:column;gap:${space(1)};padding:${space(2)};border:1px solid ${COLORS.border};border-radius:${RADII.xl};background:${COLORS.bgPanelLight};`
const mutedCss = css`font-size:${TYPE.caption.size};color:${COLORS.textSecondary};`
const safeCss = css`display:flex;align-items:center;gap:${space(1)};padding:${space(1)};border-radius:${RADII.lg};background:${COLORS.successSoft};color:${COLORS.success};font-size:${TYPE.caption.size};`
const statusCss = plot => css`align-self:flex-start;padding:3px 8px;border-radius:${RADII.pill};font-size:${TYPE.caption.size};letter-spacing:.08em;background:${plot.mine ? COLORS.cyanSoft : plot.claimable ? COLORS.successSoft : COLORS.warningSoft};color:${plot.mine ? COLORS.cyan : plot.claimable ? COLORS.success : COLORS.warning};`
const selectCss = css`min-height:44px;padding:0 ${space(1)};border:1px solid ${COLORS.borderStrong};border-radius:${RADII.lg};background:${COLORS.bgElevated};color:${COLORS.text};font:inherit;`
